import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { useDirtyState } from "../../hooks/useDirtyState";
import { DirtyStateDialog } from "../shared/DirtyStateDialog";

const CreateStock = ({ newStock, setNewStock, onCreate, isOpen, setIsOpen }) => {
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { markDirty, resetDirty, showDialog, confirmNavigation, cancelNavigation } = useDirtyState(newStock);
    const prevIsOpenRef = useRef(isOpen);

    useEffect(() => {
        if (!prevIsOpenRef.current && isOpen) {
            resetDirty(newStock);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, newStock, resetDirty]);

    const handleInputChange = (field, value) => {
        setNewStock(prev => {
            const updated = { ...prev, [field]: value };
            markDirty(updated);
            return updated;
        });
    };

    const validate = (data) => {
        const errs = {};
        if (!data.stock_symbol || data.stock_symbol.trim() === '') errs.stock_symbol = 'Ce champ est requis';
        else if (!/^[A-Z0-9]+$/.test(data.stock_symbol)) errs.stock_symbol = 'Format invalide (majuscules et chiffres uniquement)';
        else if (data.stock_symbol.length > 10) errs.stock_symbol = 'Maximum 10 caractères';
        if (!data.company_name || data.company_name.trim() === '') errs.company_name = 'Ce champ est requis';
        else if (data.company_name.length > 255) errs.company_name = 'Maximum 255 caractères';
        return errs;
    };

    const handleSubmit = async () => {
        const validationErrors = validate(newStock);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setIsSubmitting(true);
        try {
            await onCreate(newStock);
            resetDirty({ stock_symbol: '', company_name: '' });
            setIsOpen(false);
            setErrors({});
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setErrors({});
        resetDirty({ stock_symbol: '', company_name: '' });
    };

    if (!isOpen) {
        return (
            <Button variant="outline" onClick={() => setIsOpen(true)}>
                + Ajouter un stock
            </Button>
        );
    }

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>Ajouter un stock</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="create-symbol">Symbole boursier</Label>
                    <Input
                        id="create-symbol"
                        value={newStock.stock_symbol}
                        onChange={(e) => handleInputChange('stock_symbol', e.target.value.toUpperCase())}
                        className={errors.stock_symbol ? "border-destructive" : ""}
                    />
                    {errors.stock_symbol && (
                        <p className="text-sm text-destructive mt-1">{errors.stock_symbol}</p>
                    )}
                </div>
                <div>
                    <Label htmlFor="create-name">Nom de la société</Label>
                    <Input
                        id="create-name"
                        value={newStock.company_name}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        className={errors.company_name ? "border-destructive" : ""}
                    />
                    {errors.company_name && (
                        <p className="text-sm text-destructive mt-1">{errors.company_name}</p>
                    )}
                </div>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row gap-2 justify-end">
                <Button
                    variant="default"
                    disabled={isSubmitting || Object.keys(validate(newStock)).length > 0}
                    onClick={handleSubmit}
                >
                    {isSubmitting ? (
                        <>
                            <Spinner className="h-4 w-4 mr-2" />
                            Ajout en cours...
                        </>
                    ) : (
                        "Ajouter"
                    )}
                </Button>
                <Button
                    variant="outline"
                    onClick={handleClose}
                >
                    Annuler
                </Button>
            </CardFooter>
            <DirtyStateDialog open={showDialog} onConfirm={confirmNavigation} onCancel={cancelNavigation} />
        </Card>
    );
};

export default CreateStock;
